// @flow

import React, { Fragment, Component } from "react";
import {
    Switch,
    Route,
    Redirect,
    withRouter,
    type Match,
    type RouterHistory,
    type Location,
} from "react-router";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

import stripslash from "../../../utils/stripslash";

import { type State as DetailsState } from "../../../redux/modules/details.d";
import { load, clear, ERROR } from "../../../redux/modules/details";
import { load as retoolLoad } from "../../../redux/modules/embed-retool-organisation";

import { mapState } from "../../../redux/utils";

import NotFound from "../../../components/NotFound";
import TabbedHeader from "../../../components/Authenticated/TabbedHeader";

import { withAcl } from "../../Acl";
import { withTranslate } from "../../Translate";
import Details from "./Details";
import Lines from "./Lines";
import Members from "./Members";
import Retool from "react-retool";

type Props = {
    acl: *,
    translate: *,
    match: Match,
    history: RouterHistory,
    details: DetailsState,
    load: (organisationUUID: string) => void,
    retoolLoad: (organisationUUID: string) => void,
    trainingUrl: ?string,
    clear: () => void,
    location: Location,
};

type State = {
    selected: number,
};

const TABS = (translate, acl) => {
    const tabs = [];

    if (acl("organisations.members.read")) {
        tabs.push(["/members/page/1/", translate("Members"), "members"]);
    }

    if (acl("organisations.lines.read")) {
        tabs.push(["/lines/page/1/", translate("Preferred lines"), "lines"]);
    }

    if (acl("organisations.read")) {
        tabs.push(["/details/", translate("Details"), "details"]);
    }

    if (acl("organisations.create")) {
        tabs.push(["/training/", "Training", "training"]);
    }

    return tabs;
};

class Detail extends Component<Props, State> {
    state = {
        selected: 0,
    };

    static getDerivedStateFromProps(props: Props) {
        const path = props.location.pathname;
        const tabs = TABS(props.translate, props.acl);
        const found = tabs.findIndex(tab => path.includes(tab[2]));
        return {
            selected: Math.max(Math.min(found, tabs.length - 1), 0),
        };
    }

    /**
     * Initial load
     */
    componentDidMount() {
        const { clear, retoolLoad, match, location } = this.props;
        clear();
        this.reload();
        if (match.params.id && location.pathname.includes("/training/")) {
            retoolLoad(match.params.id);
        }
    }

    /**
     * Refresh
     */
    componentDidUpdate(prevProps) {
        this.reload();
        const { match, retoolLoad, location } = this.props;
        const isNowTraining = location.pathname.includes("/training/");
        const wasTraining = prevProps.location.pathname.includes("/training/");
        if (match.params.id && isNowTraining && !wasTraining) {
            retoolLoad(match.params.id);
        }
    }

    /**
     * Trigger update
     */
    reload() {
        const { load, match } = this.props;
        if (match.params.id) {
            load(match.params.id);
        }
    }

    /**
     * Handle tab change
     */
    handleTabChange(tabIndex: number) {
        const { translate, acl, history, match } = this.props;
        history.push(
            `${stripslash(match.url)}${TABS(translate, acl)[tabIndex][0]}`,
        );
        this.setState({
            selected: tabIndex,
        });
    }

    /**
     * Render
     */
    render() {
        const { match, history, details, translate, acl, trainingUrl } =
            this.props;
        const { selected } = this.state;

        return (
            <Fragment>
                <TabbedHeader
                    id="detail-header"
                    title={details.data ? details.data.name : "..."}
                    subtitle={translate("Organisations")}
                    tabs={TABS(translate, acl).map(tab => tab[1])}
                    active={selected}
                    onChange={this.handleTabChange.bind(this)}
                />
                <Switch>
                    <Route path={`${match.path}members/page/:page/`}>
                        {(acl("organisations.members.read") && (
                            <Members
                                onTabChange={path =>
                                    history.push(
                                        `${stripslash(match.url)}${path}`,
                                    )
                                }
                                onPageChange={page =>
                                    history.push(
                                        `${stripslash(
                                            match.url,
                                        )}/members/page/${page + 1}/`,
                                    )
                                }
                            />
                        )) || <NotFound translate={translate} />}
                    </Route>
                    <Route path={`${match.path}lines/page/:page/`}>
                        {(acl("organisations.lines.read") && (
                            <Lines
                                onTabChange={path =>
                                    history.push(
                                        `${stripslash(match.url)}${path}`,
                                    )
                                }
                                onPageChange={page =>
                                    history.push(
                                        `${stripslash(
                                            match.url,
                                        )}/lines/page/${page}/`,
                                    )
                                }
                            />
                        )) || <NotFound translate={translate} />}
                    </Route>
                    <Route path={`${match.path}details/`} exact>
                        {(acl("organisations.read") && (
                            <Details
                                onTabChange={path =>
                                    history.push(
                                        `${stripslash(match.url)}${path}`,
                                    )
                                }
                            />
                        )) || <NotFound translate={translate} />}
                    </Route>
                    <Route path={`${match.path}training/`} exact>
                        {(acl("organisations.create") && (
                            <Retool
                                styling={{
                                    display: "block",
                                    width: "100%",
                                    height: "80vh",
                                }}
                                url={trainingUrl}
                            />
                        )) || <NotFound translate={translate} />}
                    </Route>
                    <Route>
                        <Redirect
                            to={`${stripslash(match.url)}/members/page/1/`}
                        />
                    </Route>
                </Switch>
            </Fragment>
        );
    }
}

export default withAcl(
    withTranslate(
        withRouter(
            connect(
                ({ details, embedRetoolOrganisation }) => ({
                    details: {
                        ...mapState(details, ERROR),
                        uuid:
                            details.params &&
                            details.params.path &&
                            details.params.path.uuid,
                    },
                    trainingUrl: embedRetoolOrganisation.data,
                }),
                (dispatch: *) =>
                    bindActionCreators({ load, clear, retoolLoad }, dispatch),
            )(Detail),
        ),
    ),
);
